// Ultimate Card Game (UCG) - Player Taunts & Emotes Controller
//
// Manages the Hearthstone-style commander taunt selection popup, speech bubbles,
// audio triggers, and network dispatching for both local and online multiplayer.

import { getEquippedTaunts, getTauntById } from '../data/taunts.js';
import { inventory } from '../engine/inventory.js';
import { progress } from '../engine/progress.js';
import { resolveAvatarImageUrl } from './avatarImage.js';
import { getAvatarById } from '../engine/avatarCatalog.js';
import { getIconSvg } from './icons.js';

export class TauntManager {
  static COOLDOWN_MS = 3500;
  static AUTO_CLOSE_MS = 5000;
  static BUBBLE_DURATION_MS = 2800;
  static CUTIN_DURATION_MS = 2150;

  constructor(game) {
    this.game = game;
    this.playerTeam = 'western';

    this.isOpen = false;
    this.cooldownUntil = 0;

    this._autoCloseTimer = null;
    this._bubbleTimers = { western: null, eastern: null };

    this.layerEl = null;
    this.backdropEl = null;
    this.menuEl = null;
    this.bubbles = { western: null, eastern: null };
    this.cutinLayerEl = null;
    this._cutinTimers = { western: null, eastern: null };

    this._boundOnKeyDown = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
    };

    this.initDOM();
    this.bindEvents();
  }

  setPlayerTeam(team) {
    if (team === 'western' || team === 'eastern') {
      this.playerTeam = team;
      this.updateAvatarInteractivity();
    }
  }

  initDOM() {
    if (typeof document === 'undefined') return;

    let layer = document.getElementById('taunt-system-layer');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'taunt-system-layer';
      layer.className = 'taunt-system-layer';
      const wrapper = document.body || document.getElementById('game-wrapper');
      if (wrapper) wrapper.appendChild(layer);
    }
    this.layerEl = layer;

    // Dedicated Cut-In Banner Layer (independent of speech bubble layer)
    let cutinLayer = document.getElementById('taunt-cutin-layer');
    if (!cutinLayer) {
      cutinLayer = document.createElement('div');
      cutinLayer.id = 'taunt-cutin-layer';
      const wrapper = document.getElementById('game-wrapper') || document.body;
      if (wrapper) wrapper.appendChild(cutinLayer);
    }
    this.cutinLayerEl = cutinLayer;

    // 1. Transparent click-outside backdrop
    let backdrop = document.getElementById('taunt-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'taunt-backdrop';
      backdrop.className = 'taunt-backdrop hidden';
      this.layerEl.appendChild(backdrop);
    }
    this.backdropEl = backdrop;

    // 2. Selection Menu Panel
    let menu = document.getElementById('taunt-selection-menu');
    if (!menu) {
      menu = document.createElement('div');
      menu.id = 'taunt-selection-menu';
      menu.className = 'taunt-selection-menu hidden';
      this.layerEl.appendChild(menu);
    }
    this.menuEl = menu;

    // 3. Speech Bubbles for Western and Eastern commanders
    for (const team of ['western', 'eastern']) {
      let bubble = document.getElementById(`taunt-bubble-${team}`);
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = `taunt-bubble-${team}`;
        bubble.className = `taunt-speech-bubble team-${team} hidden`;
        this.layerEl.appendChild(bubble);
      }
      this.bubbles[team] = bubble;
    }
  }

  bindEvents() {
    // Backdrop click or tap dismisses the menu without triggering other game actions
    if (this.backdropEl) {
      let lastBackdropDismiss = 0;
      const dismiss = (e) => {
        const now = Date.now();
        if (now - lastBackdropDismiss < 300) return;
        lastBackdropDismiss = now;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        this.closeMenu();
      };
      this.backdropEl.addEventListener('click', dismiss);
      this.backdropEl.addEventListener('pointerdown', dismiss);
    }

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('keydown', this._boundOnKeyDown);
      window.addEventListener('resize', () => {
        if (this.isOpen) this.updateMenuPosition();
      });
    }

    // Bind avatar click handlers
    this.updateAvatarInteractivity();
  }

  /**
   * Configures click listeners and visual attributes on the own avatar,
   * removing them from the opponent's avatar.
   */
  updateAvatarInteractivity() {
    if (typeof document === 'undefined') return;

    const westAvatar = document.querySelector('.boss-avatar.western');
    const eastAvatar = document.querySelector('.boss-avatar.eastern');

    const myAvatar = this.playerTeam === 'eastern' ? eastAvatar : westAvatar;
    const enemyAvatar = this.playerTeam === 'eastern' ? westAvatar : eastAvatar;

    if (enemyAvatar) {
      enemyAvatar.classList.remove('player-controllable');
      enemyAvatar.removeAttribute('aria-haspopup');
      enemyAvatar.removeAttribute('title');
      enemyAvatar.removeAttribute('role');
      enemyAvatar.removeAttribute('tabindex');
      if (enemyAvatar._tauntHandler) {
        enemyAvatar.removeEventListener('click', enemyAvatar._tauntHandler);
        if (enemyAvatar._tauntPointerDown) enemyAvatar.removeEventListener('pointerdown', enemyAvatar._tauntPointerDown);
        if (enemyAvatar._tauntPointerUp) enemyAvatar.removeEventListener('pointerup', enemyAvatar._tauntPointerUp);
        if (enemyAvatar._tauntKeyDown) enemyAvatar.removeEventListener('keydown', enemyAvatar._tauntKeyDown);
        enemyAvatar._tauntHandler = null;
        enemyAvatar._tauntPointerDown = null;
        enemyAvatar._tauntPointerUp = null;
        enemyAvatar._tauntKeyDown = null;
      }
    }

    if (myAvatar) {
      myAvatar.classList.add('player-controllable');
      myAvatar.setAttribute('aria-haspopup', 'menu');
      myAvatar.setAttribute('title', 'Commander Taunts (Click or tap to open)');
      myAvatar.setAttribute('role', 'button');
      myAvatar.setAttribute('tabindex', '0');

      if (!myAvatar._tauntHandler) {
        let touchStartX = 0;
        let touchStartY = 0;
        let touchStartTime = 0;
        let lastTriggerTime = 0;

        const triggerAction = (e) => {
          const now = Date.now();
          if (now - lastTriggerTime < 350) return;
          lastTriggerTime = now;
          if (e.cancelable) e.preventDefault();
          e.stopPropagation();
          this.handleAvatarClick();
        };

        myAvatar._tauntPointerDown = (e) => {
          touchStartX = e.clientX ?? 0;
          touchStartY = e.clientY ?? 0;
          touchStartTime = Date.now();
        };

        myAvatar._tauntPointerUp = (e) => {
          if (e.pointerType === 'touch' || e.pointerType === 'pen') {
            const dx = (e.clientX ?? 0) - touchStartX;
            const dy = (e.clientY ?? 0) - touchStartY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const duration = Date.now() - touchStartTime;
            if (dist < 14 && duration < 500) {
              triggerAction(e);
            }
          }
        };

        myAvatar._tauntHandler = (e) => {
          triggerAction(e);
        };

        myAvatar._tauntKeyDown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleAvatarClick();
          }
        };

        myAvatar.addEventListener('pointerdown', myAvatar._tauntPointerDown);
        myAvatar.addEventListener('pointerup', myAvatar._tauntPointerUp);
        myAvatar.addEventListener('click', myAvatar._tauntHandler);
        myAvatar.addEventListener('keydown', myAvatar._tauntKeyDown);
      }
    }
  }

  handleAvatarClick() {
    // Block if game is in exhibition, paused, game over, or a full modal is open
    if (this.game?.isExhibition) return;
    if (this.game?.isPaused) return;
    if (this.isModalOpen()) return;

    if (this.isOpen) {
      this.closeMenu();
      return;
    }

    this.openMenu();
  }

  isModalOpen() {
    if (typeof document === 'undefined') return false;
    const activeModal = document.querySelector('.modal-container.active');
    return !!activeModal;
  }

  openMenu() {
    const now = Date.now();
    const isCooldown = now < this.cooldownUntil;

    this.renderMenuOptions(isCooldown);
    this.updateMenuPosition();

    this.isOpen = true;
    this.backdropEl?.classList.remove('hidden');
    this.menuEl?.classList.remove('hidden');
    this.menuEl?.classList.add('active');

    // Auto-dismiss after 5.0 seconds of inactivity
    clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = setTimeout(() => {
      this.closeMenu();
    }, TauntManager.AUTO_CLOSE_MS);
  }

  closeMenu() {
    if (!this.isOpen) return;
    this.isOpen = false;
    clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = null;

    if (this.menuEl) {
      this.menuEl.classList.remove('active');
      this.menuEl.classList.add('hidden');
    }
    if (this.backdropEl) {
      this.backdropEl.classList.add('hidden');
    }
  }

  updateMenuPosition() {
    if (!this.menuEl || typeof document === 'undefined') return;

    const selector = `.boss-avatar.${this.playerTeam}`;
    const avatarEl = document.querySelector(selector);
    if (!avatarEl) return;

    const rect = avatarEl.getBoundingClientRect();
    const isEastern = this.playerTeam === 'eastern';

    // Position menu anchored near avatar portrait, clearing the directives micro-chip
    const top = Math.round(rect.bottom + 8);
    this.menuEl.style.top = `${top}px`;

    if (isEastern) {
      const right = Math.round(window.innerWidth - rect.right);
      this.menuEl.style.right = `${Math.max(8, right)}px`;
      this.menuEl.style.left = 'auto';
      this.menuEl.classList.add('team-eastern');
      this.menuEl.classList.remove('team-western');
    } else {
      const left = Math.round(rect.left);
      this.menuEl.style.left = `${Math.max(8, left)}px`;
      this.menuEl.style.right = 'auto';
      this.menuEl.classList.add('team-western');
      this.menuEl.classList.remove('team-eastern');
    }
  }

  renderMenuOptions(isCooldown = false) {
    if (!this.menuEl) return;

    const taunts = getEquippedTaunts(inventory);
    const cooldownRemainingSec = Math.max(0, Math.ceil((this.cooldownUntil - Date.now()) / 1000));

    let html = `
      <div class="taunt-menu-panel ${isCooldown ? 'cooldown-active' : ''}">
        <div class="taunt-menu-header">
          <span class="taunt-menu-title">TACTICAL COMMS</span>
          ${isCooldown ? `<span class="taunt-cooldown-badge">CD: ${cooldownRemainingSec}s</span>` : ''}
        </div>
        <div class="taunt-pills-list">
    `;

    for (const t of taunts) {
      html += `
        <button class="taunt-pill ${isCooldown ? 'disabled' : ''}" data-taunt-id="${t.id}" ${isCooldown ? 'disabled' : ''}>
          <span class="taunt-icon">${t.icon || getIconSvg('bubble', 14)}</span>
          <span class="taunt-text">${t.text}</span>
        </button>
      `;
    }

    html += `
        </div>
      </div>
    `;

    this.menuEl.innerHTML = html;

    // Attach click listeners to pills
    const pillButtons = this.menuEl.querySelectorAll('.taunt-pill');
    pillButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const tauntId = btn.getAttribute('data-taunt-id');
        this.selectTaunt(tauntId);
      });
    });
  }

  selectTaunt(tauntId) {
    if (!tauntId) return;
    const now = Date.now();
    if (now < this.cooldownUntil) {
      return;
    }

    // 1. Close menu immediately
    this.closeMenu();

    // 2. Set cooldown (3.5s)
    this.cooldownUntil = now + TauntManager.COOLDOWN_MS;

    // 3. Generate unique command ID for idempotency across P2P & Lockstep
    const cmdId = `taunt_${this.playerTeam}_${now}_${Math.floor(Math.random() * 100000)}`;

    // 4. Record as applied locally so when the turn comes back it won't duplicate
    if (this.game?.commandProcessor?.appliedCommandIds) {
      this.game.commandProcessor.appliedCommandIds.add(cmdId);
    }

    // 5. Display speech bubble immediately on local screen
    this.showTauntBubble(this.playerTeam, tauntId);

    // 6. Network Dispatch
    if (this.game?.isOnline) {
      const payload = {
        type: 'TAUNT',
        id: cmdId,
        team: this.playerTeam,
        text: tauntId,
        tauntId: tauntId
      };

      // Primary WebSocket match server (Authoritative 1v1 and 2v2)
      if (this.game.wsClient?.isMatchActive) {
        this.game.wsClient.send(payload);
      }

      // Legacy fallback: WebRTC DataChannel / Lockstep queue
      if (this.game.turnScheduler?.isP2PActive) {
        this.game.turnScheduler.peerManager?.sendTaunt?.(payload);
      }
      this.game.turnScheduler?.recordLocalCommand?.(payload);
    }
  }

  /**
   * Displays the animated comic Cut-In banner and triggers audio for either team.
   * Retained for 100% backward compatibility across network & simulation calls.
   * @param {'western'|'eastern'} team
   * @param {string} tauntId
   */
  showTauntBubble(team, tauntId) {
    if (typeof document === 'undefined') return;

    // 1. Audio trigger (WebAudio synthesized comic cut-in SFX)
    this.game?.audio?.playTauntBlip?.(tauntId);

    // 2. Trigger high-impact Commander Cut-In & Speedline Banner
    this.showCutInBanner(team, tauntId);

    // 3. Keep speech bubble updated as fallback / accessibility indicator
    const bubbleEl = this.bubbles?.[team];
    if (bubbleEl) {
      const def = getTauntById(tauntId) || { text: tauntId, icon: getIconSvg('bubble', 14) };
      bubbleEl.innerHTML = `
        <div class="taunt-bubble-inner">
          <span class="bubble-chevron" aria-hidden="true"></span>
          <span class="bubble-icon">${def.icon || getIconSvg('bubble', 14)}</span>
          <span class="bubble-msg">${def.text}</span>
        </div>
      `;
    }
  }

  /**
   * Ensures any active or newly spawned Cut-In Banner lands vertically below
   * an active Hacker 5-card reveal (or opponent plasma capsule) so it never
   * obscures the revealed hand.
   */
  syncCutInVerticalOffset() {
    if (typeof document === 'undefined') return;
    const layer = this.cutinLayerEl || document.getElementById('taunt-cutin-layer');
    if (!layer || !layer.style) return;

    const revealedHand = document.querySelector('.opponent-revealed-hand:not(.hidden)');
    if (revealedHand && typeof revealedHand.getBoundingClientRect === 'function') {
      const rect = revealedHand.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0) {
        layer.style.setProperty('--taunt-cutin-top', `${Math.round(rect.bottom + 8)}px`);
        return;
      }
    }

    const plasmaCapsule = document.querySelector('.opponent-plasma-capsule:not(.hidden)');
    if (plasmaCapsule && typeof plasmaCapsule.getBoundingClientRect === 'function') {
      const rect = plasmaCapsule.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0) {
        if (typeof layer.style?.setProperty === 'function') {
          layer.style.setProperty('--taunt-cutin-top', `${Math.round(rect.bottom + 8)}px`);
        } else if (layer.style) {
          layer.style['--taunt-cutin-top'] = `${Math.round(rect.bottom + 8)}px`;
        }
        return;
      }
    }

    if (typeof layer.style?.removeProperty === 'function') {
      layer.style.removeProperty('--taunt-cutin-top');
    } else if (layer.style) {
      delete layer.style['--taunt-cutin-top'];
    }
  }

  /**
   * Displays the comic-style Commander Cut-In & Speedline Banner for either team.
   * @param {'western'|'eastern'} team
   * @param {string} tauntId
   */
  showCutInBanner(team, tauntId) {
    if (typeof document === 'undefined') return;

    if (!this.cutinLayerEl) {
      this.cutinLayerEl = document.getElementById('taunt-cutin-layer');
      if (!this.cutinLayerEl) {
        this.initDOM();
      }
    }
    if (!this.cutinLayerEl) return;

    this.syncCutInVerticalOffset();

    const def = getTauntById(tauntId) || {
      text: tauntId,
      icon: getIconSvg('bubble', 14),
      accentIcon: getIconSvg('energy', 14),
      callout: 'TACTICAL COMMS',
      themeColor: team === 'western' ? '#00e5ff' : '#ff2255',
      glowColor: team === 'western' ? 'rgba(0, 229, 255, 0.85)' : 'rgba(255, 34, 85, 0.85)',
      sparkGlyphs: ['✦', '★', '▲']
    };

    // 1. Clear any active cut-in banner for this team to prevent overlapping animations
    if (this._cutinTimers[team]) {
      clearTimeout(this._cutinTimers[team]);
      this._cutinTimers[team] = null;
    }
    const existing = this.cutinLayerEl.querySelector(`.taunt-cutin-banner.team-${team}`);
    if (existing) {
      existing.remove();
    }

    // 2. Create Cut-In Banner container
    const banner = document.createElement('div');
    banner.className = `taunt-cutin-banner team-${team}`;
    banner.style.setProperty('--cutin-border', def.themeColor || (team === 'western' ? '#00e5ff' : '#ff2255'));
    banner.style.setProperty('--cutin-glow', def.glowColor || (team === 'western' ? 'rgba(0, 229, 255, 0.85)' : 'rgba(255, 34, 85, 0.85)'));

    // 3. Commander Close-Up Avatar Panel
    const avatarPanel = document.createElement('div');
    avatarPanel.className = 'cutin-avatar-panel';

    const avatarCanvas = document.createElement('canvas');
    avatarCanvas.className = 'cutin-avatar-canvas';
    avatarCanvas.width = 68;
    avatarCanvas.height = 68;

    const sourceCanvas = document.getElementById(`${team}-avatar-canvas`) || document.querySelector(`.boss-avatar.${team} canvas`);
    if (sourceCanvas && typeof avatarCanvas.getContext === 'function') {
      try {
        const ctx = avatarCanvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(sourceCanvas, 0, 0, 68, 68);
        }
      } catch (e) {
        // Fallback gracefully if context unavailable in headless env
      }
    }
    avatarPanel.appendChild(avatarCanvas);

    // Custom commander avatar image (from Arsenal > Progress > Level perk, docs/XP_PROGRESSION_PLAN.md 6.1)
    const customImg = document.getElementById(`${team}-avatar-custom-image`) || document.querySelector(`.boss-avatar.${team} .boss-avatar-custom-image`);
    let customSrc = (customImg && !customImg.classList.contains('hidden') && customImg.getAttribute('src'))
      ? (customImg.getAttribute('src') || customImg.src)
      : null;

    if (!customSrc) {
      const isPlayerTeam = this.playerTeam === team;
      if (isPlayerTeam) {
        try {
          const equipped = progress.getEquippedAvatar();
          if (equipped) {
            customSrc = resolveAvatarImageUrl(equipped);
          }
        } catch (e) {}
      } else if (this.game?.isOnline && this.game?.opponentOnlineAvatarId) {
        try {
          const oppAvatar = getAvatarById(this.game.opponentOnlineAvatarId);
          if (oppAvatar) {
            customSrc = resolveAvatarImageUrl(oppAvatar);
          }
        } catch (e) {}
      }
    }

    if (customSrc) {
      const avatarImg = document.createElement('img');
      avatarImg.className = 'cutin-avatar-image';
      avatarImg.src = customSrc;
      avatarImg.alt = '';
      avatarPanel.appendChild(avatarImg);
    }

    banner.appendChild(avatarPanel);

    // 4. Content Panel (Callout Tag + Comic Main Row + Rotating Stars)
    const contentPanel = document.createElement('div');
    contentPanel.className = 'cutin-content-panel';

    const callout = document.createElement('span');
    callout.className = 'cutin-callout-tag';
    callout.innerText = def.callout || 'TACTICAL COMMS';
    contentPanel.appendChild(callout);

    const mainRow = document.createElement('div');
    mainRow.className = 'cutin-main-row';

    const leftStar = document.createElement('span');
    leftStar.className = 'cutin-star';
    leftStar.setAttribute('aria-hidden', 'true');
    leftStar.innerHTML = def.accentIcon || getIconSvg('energy', 14);

    const textEl = document.createElement('span');
    textEl.className = 'cutin-comic-text';
    textEl.innerText = def.text;

    const rightStar = document.createElement('span');
    rightStar.className = 'cutin-star';
    rightStar.setAttribute('aria-hidden', 'true');
    rightStar.innerHTML = def.accentIcon || getIconSvg('energy', 14);

    mainRow.appendChild(leftStar);
    mainRow.appendChild(textEl);
    mainRow.appendChild(rightStar);
    contentPanel.appendChild(mainRow);
    banner.appendChild(contentPanel);

    // 5. Horizontal Anamorphic Lens Flare Sweep
    const flare = document.createElement('div');
    flare.className = 'cutin-lens-flare';
    flare.setAttribute('aria-hidden', 'true');
    banner.appendChild(flare);

    // 6. Radial Flying Sparks (Reconsider/Melt heritage)
    const sparkGlyphs = def.sparkGlyphs || ['✦', '⚡', '★'];
    const sparkCount = 8;
    for (let i = 0; i < sparkCount; i++) {
      const spark = document.createElement('span');
      spark.className = 'cutin-spark';
      spark.innerText = sparkGlyphs[i % sparkGlyphs.length];

      const angle = (i / sparkCount) * (Math.PI * 2) + (Math.random() * 0.3 - 0.15);
      const distance = 45 + Math.random() * 65;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;
      const scale = 0.9 + Math.random() * 0.7;
      const delay = Math.random() * 60;

      spark.style.setProperty('--tx', `${tx.toFixed(1)}px`);
      spark.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      spark.style.setProperty('--s', `${scale.toFixed(2)}`);
      spark.style.animationDelay = `${delay.toFixed(0)}ms`;

      spark.style.left = team === 'western' ? '45%' : '55%';
      spark.style.top = '50%';

      banner.appendChild(spark);
    }

    // 7. Mount to Cut-In layer
    this.cutinLayerEl.appendChild(banner);

    // 8. Top HUD Micro-Impact Shake
    const hudBar = document.querySelector(`.boss-column.${team}`) || document.getElementById('top-hud');
    if (hudBar && hudBar.classList) {
      hudBar.classList.remove('taunt-hud-impact-shake');
      void hudBar.offsetWidth;
      hudBar.classList.add('taunt-hud-impact-shake');
      setTimeout(() => hudBar.classList?.remove('taunt-hud-impact-shake'), 160);
    }

    // 9. Auto-cleanup timer after animation completes (2.15s, extended hold)
    this._cutinTimers[team] = setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
      }
      this._cutinTimers[team] = null;
    }, TauntManager.CUTIN_DURATION_MS);
  }

  destroy() {
    this.closeMenu();
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('keydown', this._boundOnKeyDown);
    }
    clearTimeout(this._autoCloseTimer);
    clearTimeout(this._bubbleTimers.western);
    clearTimeout(this._bubbleTimers.eastern);
    clearTimeout(this._cutinTimers.western);
    clearTimeout(this._cutinTimers.eastern);

    if (this.cutinLayerEl) {
      this.cutinLayerEl.innerHTML = '';
    }

    if (typeof document !== 'undefined') {
      const westAvatar = document.querySelector('.boss-avatar.western');
      const eastAvatar = document.querySelector('.boss-avatar.eastern');
      for (const av of [westAvatar, eastAvatar]) {
        if (av && av._tauntHandler) {
          av.removeEventListener('click', av._tauntHandler);
          if (av._tauntPointerDown) av.removeEventListener('pointerdown', av._tauntPointerDown);
          if (av._tauntPointerUp) av.removeEventListener('pointerup', av._tauntPointerUp);
          if (av._tauntKeyDown) av.removeEventListener('keydown', av._tauntKeyDown);
          av._tauntHandler = null;
          av._tauntPointerDown = null;
          av._tauntPointerUp = null;
          av._tauntKeyDown = null;
        }
      }
    }
  }
}
